import { useState } from "react";
import { Copy, Check, Download, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PRESS } from "@/data/site";
import { CFG_VIDEO_URL } from "@/components/VideoCard";

function CopyCard({ label, text, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    let ok = false;
    try {
      if (!navigator.clipboard || !window.isSecureContext) throw new Error("no-clipboard-api");
      await navigator.clipboard.writeText(text);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (!ok) {
      toast.error("Copy failed — please select the text manually");
      return;
    }
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <article className="h-full rounded-lg border border-slate-200 bg-white p-7 flex flex-col">
      <div className="flex items-center justify-between gap-4">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">{label}</p>
        <button
          onClick={copy}
          data-testid={testid}
          aria-label={`Copy ${label} boilerplate`}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:border-emerald-400 hover:text-emerald-700"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-600 flex-1">{text}</p>
    </article>
  );
}

export default function PressKit() {
  return (
    <>
      <Seo
        title="Press Kit — BNM Sustainable Energy Limited"
        description="Media resources for BNM Sustainable Energy Limited: approved boilerplate, company facts, official logo, product photography, founder portrait and usage guidelines."
      />
      <PageHero
        testid="press-hero"
        eyebrow="Press Kit"
        lines={["Everything media", "needs, in one place."]}
        lead="Approved company descriptions, verified facts, official imagery and usage guidelines for journalists, partners and event organisers covering BNM."
      />

      <section data-testid="press-boilerplate" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Approved Boilerplate"
            title="Copy-ready company descriptions."
            lead="Use these verbatim in articles, programmes and partner materials."
          />
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <Reveal>
              <CopyCard label="Short — 50 words" text={PRESS.boilerplateShort} testid="press-copy-short" />
            </Reveal>
            <Reveal delay={0.08}>
              <CopyCard label="Standard — 120 words" text={PRESS.boilerplateStandard} testid="press-copy-standard" />
            </Reveal>
          </div>

          <Reveal delay={0.12}>
            <div className="mt-6 rounded-lg border border-slate-200 bg-white p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-600">Company Facts</p>
              <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5">
                {PRESS.facts.map((f) => (
                  <div key={f.label}>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">{f.label}</dt>
                    <dd className="mt-1.5 text-sm font-medium text-slate-900">{f.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="press-assets" className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Download Assets"
            title="Official imagery, ready to publish."
            lead="All files are official BNM assets. Click any item to download the full-resolution file."
          />
          <div className="mt-12 space-y-14">
            {PRESS.groups.map((group, gi) => (
              <Reveal key={group.title} delay={gi * 0.05}>
                <div>
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <h3 className="font-display text-xl font-bold tracking-tight text-slate-900">{group.title}</h3>
                    <p className="text-xs text-slate-500">{group.note}</p>
                  </div>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.items.map((item) => (
                      <a
                        key={item.file}
                        href={item.file}
                        download
                        data-testid={`press-download-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="group rounded-lg border border-slate-200 bg-mist overflow-hidden transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-emerald-300 hover:shadow-lg"
                      >
                        <span className="block overflow-hidden bg-white">
                          <img
                            src={item.file}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                          />
                        </span>
                        <span className="flex items-center justify-between gap-3 p-4">
                          <span>
                            <span className="block text-sm font-semibold text-slate-900">{item.name}</span>
                            <span className="block mt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-400">
                              {item.format} · Full resolution
                            </span>
                          </span>
                          <Download className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}

            <Reveal delay={0.1}>
              <div className="rounded-lg border border-slate-200 bg-mist p-7 flex flex-wrap items-center justify-between gap-5">
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">Video Footage</h3>
                  <p className="mt-1.5 text-sm text-slate-600">
                    Official video of the Carbon Free Generator in operation is published on the BNM Facebook channel.
                  </p>
                </div>
                <a
                  href={CFG_VIDEO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="press-video-link"
                  className="inline-flex items-center gap-2 rounded-md bg-obsidian px-6 py-3 text-sm font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt"
                >
                  Watch on Facebook
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="press-guidelines" className="bg-mist grid-lines-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead eyebrow="Usage Guidelines" title="Using BNM materials correctly." />
          <Reveal delay={0.1}>
            <ul className="mt-10 max-w-3xl space-y-4">
              {PRESS.guidelines.map((g, i) => (
                <li key={i} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-5">
                  <ShieldCheck className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" aria-hidden="true" />
                  <p className="text-sm leading-relaxed text-slate-700">{g}</p>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Media contact"
        text="For interviews, filming requests, high-resolution assets or corrections, contact the BNM media team directly."
        primary={{ label: "Contact the Media Team", to: "/contact", testid: "press-contact-cta" }}
        testid="press-cta"
      />
    </>
  );
}
